import { Link } from "@/lib/router-compat";
import AudienceLandingPage, { Block, AudienceSection } from "@/components/site/AudienceLandingPage";

const L = { color: "#8B1A1A" };

const intro: Block[] = [
  {
    type: "p",
    text: (
      <>
        If you are searching for outpatient rehab near you in South Jersey, Cherry Hill Recovery Center is a
        licensed outpatient program at 1930 Marlton Pike East in Cherry Hill, NJ — minutes from Route 73 and
        I-295. We provide PHP, IOP, evening IOP, virtual IOP and medication-assisted treatment, so you can get
        real clinical care without leaving your job, your family or your home.
      </>
    ),
  },
  {
    type: "p",
    text: (
      <>
        We work with most major insurance plans, and the benefits check is free and takes about a minute. Call{" "}
        <a href="tel:8562003127" style={L}>856-200-3127</a> or{" "}
        <Link to="/verify-insurance" style={L}>verify your benefits online</Link>.
      </>
    ),
  },
];

const sections: AudienceSection[] = [
  {
    h2: "What Outpatient Rehab Near Cherry Hill Looks Like",
    blocks: [
      {
        type: "p",
        text: (
          <>
            Outpatient does not mean light. It means you sleep at home and attend structured clinical
            programming during the day or evening. Your assessment determines which level fits.
          </>
        ),
      },
      {
        type: "ul",
        items: [
          <>
            <Link to="/php-cherry-hill-nj" style={L}>PHP (Partial Hospitalization)</Link> — five to six days a
            week, the most intensive level we provide, common right after detox.
          </>,
          <>
            <Link to="/iop-cherry-hill-nj" style={L}>IOP (Intensive Outpatient)</Link> — three to four sessions
            a week, group and individual therapy, designed around work.
          </>,
          <>
            <Link to="/evening-iop-new-jersey" style={L}>Evening IOP</Link> — groups that start after 5pm so you
            never have to explain a mid-day absence.
          </>,
          <>
            <Link to="/virtual-iop-new-jersey" style={L}>Virtual IOP</Link> — the same program by secure video,
            available anywhere in New Jersey.
          </>,
          <>
            <Link to="/medication-assisted-treatment-nj" style={L}>MAT</Link> — Suboxone, Sublocade or Vivitrol
            prescribed and managed alongside therapy.
          </>,
        ],
      },
    ],
  },
  {
    h2: "Outpatient vs. Inpatient Rehab",
    tone: "cream",
    blocks: [
      {
        type: "p",
        text: (
          <>
            Residential rehab bills for housing, meals and 24-hour staffing on top of clinical care and requires
            you to step out of your life for 30 days or more. Outpatient programs bill only for the clinical
            hours you attend, because you live at home.
          </>
        ),
      },
      {
        type: "ul",
        items: [
          <>Keep working, keep your income and stay with your family while you get treatment.</>,
          <>Practice recovery in the environment you actually live in, not a facility bubble.</>,
          <>
            Step up or down as needed — PHP to IOP to{" "}
            <Link to="/find-your-level-of-care" style={L}>ongoing outpatient care</Link> — without changing
            providers.
          </>,
          <>
            <Link to="/recovery-housing-nj" style={L}>Recovery housing</Link> is available if home is not a safe
            or stable place to be right now.
          </>,
        ],
      },
      {
        type: "p",
        text: (
          <>
            If your assessment shows you need detox or inpatient care first, we say so and{" "}
            <Link to="/detox-referral-coordination-nj" style={L}>help coordinate that referral</Link>.
          </>
        ),
      },
    ],
  },
  {
    h2: "What Your First Week Looks Like",
    blocks: [
      {
        type: "ul",
        items: [
          <>One phone call, day one. A free confidential clinical assessment, usually the same or next day.</>,
          <>A benefits check while you are on the phone, so you know your coverage before you start.</>,
          <>
            A level-of-care recommendation in writing — PHP, IOP or evening IOP — plus a schedule that fits your
            work hours.
          </>,
          <>
            A <Link to="/psychiatric-evaluation-nj" style={L}>psychiatric evaluation</Link> if depression,
            anxiety, trauma or bipolar symptoms are part of what is going on.
          </>,
          <>Most patients are in a group within a few days of that first call.</>,
        ],
      },
    ],
  },
  {
    h2: "Serving Camden, Burlington and Gloucester Counties",
    tone: "cream",
    blocks: [
      {
        type: "ul",
        items: [
          <>
            Camden County —{" "}
            <Link to="/addiction-treatment-voorhees-nj" style={L}>Voorhees</Link>,{" "}
            <Link to="/addiction-treatment-haddonfield-nj" style={L}>Haddonfield</Link>,{" "}
            <Link to="/addiction-treatment-pennsauken-nj" style={L}>Pennsauken</Link>,{" "}
            <Link to="/addiction-treatment-camden-nj" style={L}>Camden</Link>.
          </>,
          <>
            Burlington County —{" "}
            <Link to="/addiction-treatment-marlton-nj" style={L}>Marlton</Link>,{" "}
            <Link to="/addiction-treatment-mount-laurel-nj" style={L}>Mount Laurel</Link>,{" "}
            <Link to="/addiction-treatment-moorestown-nj" style={L}>Moorestown</Link>.
          </>,
          <>
            Gloucester County —{" "}
            <Link to="/addiction-treatment-turnersville-nj" style={L}>Turnersville</Link>,{" "}
            <Link to="/addiction-treatment-sewell-nj" style={L}>Sewell</Link>,{" "}
            <Link to="/addiction-treatment-glassboro-nj" style={L}>Glassboro</Link>.
          </>,
          <>
            Anywhere else in New Jersey through{" "}
            <Link to="/virtual-iop-new-jersey" style={L}>Virtual IOP</Link>.
          </>,
        ],
      },
    ],
  },
];

const faqs = [
  {
    q: "What is the closest outpatient rehab to me in South Jersey?",
    a: "Cherry Hill Recovery Center is at 1930 Marlton Pike East in Cherry Hill, NJ, off Route 70 near Route 73 and I-295, which puts us within a short drive of most of Camden, Burlington and Gloucester counties. If the commute is a problem, our virtual IOP covers all of New Jersey. Call 856-200-3127.",
  },
  {
    q: "How is outpatient rehab different from inpatient?",
    a: "Outpatient means you live at home and attend structured programming during the day or evening. Inpatient means residing at a facility with 24-hour staffing. Outpatient lets you keep working and stay with family, and it generally costs less because there is no housing or round-the-clock staffing being billed.",
  },
  {
    q: "Do you take insurance?",
    a: "We work with most major insurance plans. Our admissions team will verify your benefits for free, explain exactly what is covered for PHP, IOP and MAT, and give you real numbers before you commit to anything. Call 856-200-3127 or verify online.",
  },
  {
    q: "How quickly can I start outpatient treatment?",
    a: "Most people complete a free clinical assessment within a day of calling and begin group within a few days. If your assessment shows you need medical detox first, we help coordinate that placement and admit you into PHP or IOP as soon as you are discharged.",
  },
  {
    q: "Can I keep working while in outpatient rehab?",
    a: "Yes — that is the point of the schedule. Evening IOP groups start after 5pm, and virtual IOP removes the commute entirely. PHP is more time-intensive, so we talk through work logistics openly during your assessment.",
  },
];

const OutpatientRehabNearMeNJ = () => (
  <AudienceLandingPage
    slug="/outpatient-rehab-near-me-nj"
    label="Outpatient Programs"
    h1="Outpatient Rehab Near Me — Cherry Hill, NJ"
    subheadline="PHP, IOP, evening IOP and virtual IOP in Cherry Hill for Camden, Burlington and Gloucester counties. Most major insurance accepted."
    breadcrumbName="Outpatient Rehab Near Me"
    intro={intro}
    sections={sections}
    faqs={faqs}
    schemaName="Cherry Hill Recovery Center — Outpatient Rehab Near Cherry Hill, NJ"
    schemaDescription="Outpatient addiction treatment near Cherry Hill, NJ — PHP, IOP, evening IOP, virtual IOP and MAT. Free assessment, most major insurance accepted."
    ctaHeadline="Outpatient care that fits your life — start with one call."
  />
);

export default OutpatientRehabNearMeNJ;
